import { useContext, useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { HeaderFixed } from '@/components';
import { ContentSwitcher } from '@/design-system';
import { GET_USER_FAVORITE_ORGS } from '@/graphql/queries';
import type { GetUserFavoriteOrgsResponse } from '@/graphql/types';
import AuthenticationContext from '@/context/AuthenticationContext';
import { css } from '@emotion/css';
import { JarFilterButtons } from './JarFilterButtons';
import { JarSearchBox } from './JarSearchBox';
import { JarHeaderSubscriptions } from './JarHeaderSubscriptions';
import { JarHeaderUnauthenticated } from './JarHeaderUnauthenticated';
import { JAR_TABS, type JarTab } from './Jar';

interface JarHeaderProps {
  activeTab: JarTab;
  onTabChange: (tab: string) => void;
  tabGroupId: string;
}

export const JarHeader: React.FC<JarHeaderProps> = ({ activeTab, onTabChange, tabGroupId }) => {
  const { isAuthenticated } = useContext(AuthenticationContext);

  const { data: favoriteOrgsData } = useQuery<GetUserFavoriteOrgsResponse>(GET_USER_FAVORITE_ORGS, {
    fetchPolicy: 'cache-first',
    skip: !isAuthenticated,
  });

  const { orgs, isFollowingOrgs } = favoriteOrgsData?.getUserFavoriteOrgs ?? {};

  const totalNewFlyers = useMemo(
    () => orgs?.reduce((sum, org) => sum + (org.newUnreadFlyersCount ?? 0), 0) ?? 0,
    [orgs]
  );

  const showContentSwitcher = useMemo(
    () => Boolean(isAuthenticated && isFollowingOrgs),
    [isAuthenticated, isFollowingOrgs]
  );

  const tabOptions = [
    { value: JAR_TABS.ALL, label: 'All' },
    {
      value: JAR_TABS.FOLLOWING_ORGS,
      label: 'Following Orgs',
      ...(totalNewFlyers > 0 && { badge: totalNewFlyers, badgeLabel: 'new flyers' }),
    },
  ];

  return (
    <HeaderFixed className={cn.desktopReflowHeader} animatedShadow>
      {isAuthenticated ? <JarHeaderSubscriptions /> : <JarHeaderUnauthenticated />}
      <JarSearchBox />
      {showContentSwitcher && (
        <div className={cn.contentSwitcherWrapper}>
          <ContentSwitcher
            trackId="jar"
            options={tabOptions}
            selected={activeTab}
            onChange={onTabChange}
            tabGroupId={tabGroupId}
          />
        </div>
      )}
      {activeTab === JAR_TABS.ALL && <JarFilterButtons showContentSwitcher={showContentSwitcher} />}
    </HeaderFixed>
  );
};

const cn = {
  desktopReflowHeader: css`
    @media screen and (max-width: 360px) and (hover: hover) and (pointer: fine) {
      position: static;
    }
  `,
  contentSwitcherWrapper: css`
    box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.1);
  `,
};
